import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

const useAssetCategoryApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/asset-categories?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/asset-categories', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/asset-categories/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/asset-categories/${ids[0]}`);
      } else {
        await api.post('/v1/asset-categories/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('assetCategory.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'description', label: t('assetCategory.description'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('assetCategory.name'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('assetCategory.description'), type: 'textarea', grid: 12 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useAssetCategoryApi;
